import { Stack } from '@mui/material';
import { MRT_SortingState } from 'material-react-table';
import { BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import { useBikeleasingPortalLinkBuilder } from 'probonio-shared-ui/utils/bikeleasingUrl';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BenefitPaper } from '../../component/benefitTabs/BenefitPaper';
import { BenefitTab } from '../../component/benefitTabs/BenefitTab';
import { BenefitTabPanel } from '../../component/benefitTabs/BenefitTabPanel';
import { ExternalLinkButton } from '../../component/button/ExternalLinkButton';
import { useSearchParamsTabsState } from '../../component/tab/tabState';
import { getBenefitIcon } from '../../module/benefits/BenefitIcon';
import BenefitSubPage from '../../module/benefits/BenefitSubPage';
import { ContractFilterSelector } from '../../module/benefits/bikeleasingV2/ContractFilterSelector';
import { ContractPageEnum, useReduxContractFilterState } from '../../module/benefits/bikeleasingV2/contractFilterState';
import BikeleasingContractsTable from '../../module/benefits/bikeleasingV2/contracts/BikeleasingContractsTable';
import { useBikeleasingContractsViewModel } from '../../module/benefits/bikeleasingV2/contracts/useBikeleasingContractsViewModel';
import { useBikeleasingContractColumns } from '../../module/benefits/bikeleasingV2/useBikeleasingContractColumns';
import { useBikeleasingPrecondition } from '../../module/benefits/bikeleasingV2/useBikeleasingPrecondition';
import { AuthorizedLayout } from '../../module/navigation/AuthorizedLayout';
import { setContractFilter } from '../../module/userManagement/filter/filterSlice';
import { useContractList } from './useContractList';

const tabParamName = 'status';

const BikeleasingContractsPage: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { PreconditionComponent, isPreconditionMet } = useBikeleasingPrecondition();
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();
  const bikeleasingInsurancesAllUrl = buildBikeleasingPortalLink(`/user/employer/insurances/all`);

  const filterState = useReduxContractFilterState(
    useCallback(state => state.filter.contractFilter, []),
    setContractFilter,
  );

  const contractListQuery = useContractList();
  const contracts = contractListQuery.data?.results || [];
  const columns = useBikeleasingContractColumns();
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const { activeContracts, inactiveContracts, allContracts } = useBikeleasingContractsViewModel(contracts, filterState, columns, sorting);

  const hasContracts = contracts.length > 0;
  const contractCountActive = activeContracts.length;
  const contractCountInactive = inactiveContracts.length;
  const contractCountAll = allContracts.length;

  const tabsState = useSearchParamsTabsState(tabParamName);
  const tabChangeCallback = (value: string) => {
    let newContractPage: ContractPageEnum = ContractPageEnum.Active;
    if (value === ContractPageEnum.Inactive) {
      newContractPage = ContractPageEnum.Inactive;
    }
    if (value === ContractPageEnum.All) {
      newContractPage = ContractPageEnum.All;
    }

    // Update the filter state based on the selected tab
    filterState.setFilter(prev => ({
      ...prev,
      contractPage: newContractPage,
      selectedStatus: undefined,
      searchText: undefined,
    }));
  };

  const handleTabChange = (event: React.SyntheticEvent | undefined, value: string) => {
    tabsState.handleTabChange(event, value);
    tabChangeCallback(value);
  };

  // load active tabs on initial load
  useEffect(() => {
    if (!tabsState.tabIndex) {
      tabChangeCallback(ContractPageEnum.Active);
    } else {
      tabChangeCallback(tabsState.tabIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthorizedLayout
      title={t('extensions:bikeleasing_v2_benefit.contracts')}
      breadcrumbs={[{ title: t('BIKELEASING_V2.title'), to: `/benefits/${BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase()}` }]}
    >
      <BenefitSubPage
        icon={getBenefitIcon(BenefitDTOBenefitEnum.BikeleasingV2)}
        title={t('BIKELEASING_V2.title')}
        subTitle={t('extensions:bikeleasing_v2_benefit.contracts')}
        menu={
          isPreconditionMet && (
            <Stack>
              <ExternalLinkButton href={bikeleasingInsurancesAllUrl}>
                {t('extensions:bikeleasing_v2_benefit.contractAllDamageReports')}
              </ExternalLinkButton>
            </Stack>
          )
        }
      >
        {isPreconditionMet ? (
          <BenefitTabPanel tabsState={{ ...tabsState, handleTabChange: handleTabChange }}>
            <BenefitTab value="" label={t('extensions:bikeleasing_v2_benefit.contractsTable.contractsActive')} count={contractCountActive}>
              <ContractFilterSelector filterState={filterState} showStatusFilter={true} hasContracts={hasContracts} />
              <BikeleasingContractsTable
                contracts={activeContracts}
                sorting={sorting}
                onSortingChange={setSorting}
                isLoading={contractListQuery.isLoading}
              />
            </BenefitTab>
            <BenefitTab
              value={ContractPageEnum.Inactive}
              label={t('extensions:bikeleasing_v2_benefit.contractsTable.contractsInactive')}
              count={contractCountInactive}
            >
              <ContractFilterSelector filterState={filterState} showStatusFilter={true} hasContracts={hasContracts} />
              <BikeleasingContractsTable
                contracts={inactiveContracts}
                sorting={sorting}
                onSortingChange={setSorting}
                isLoading={contractListQuery.isLoading}
              />
            </BenefitTab>
            <BenefitTab
              value={ContractPageEnum.All}
              label={t('extensions:bikeleasing_v2_benefit.contractsTable.contractsAll')}
              count={contractCountAll}
            >
              <ContractFilterSelector filterState={filterState} showStatusFilter={true} hasContracts={hasContracts} />
              <BikeleasingContractsTable
                contracts={allContracts}
                sorting={sorting}
                onSortingChange={setSorting}
                isLoading={contractListQuery.isLoading}
              />
            </BenefitTab>
          </BenefitTabPanel>
        ) : (
          <BenefitPaper>{PreconditionComponent}</BenefitPaper>
        )}
      </BenefitSubPage>
    </AuthorizedLayout>
  );
};

export default BikeleasingContractsPage;
